import { Controller } from "@hotwired/stimulus"

// Logical pixels per grid cell for the canvas's natural size.
// Token positioning is percentage-based so this only sets pixel density at zoom 1.0.
const CELL_SIZE = 64

export default class extends Controller {
  static targets = ["canvas", "panel", "panelChevron", "fogCanvas", "fogPaintOverlay", "fogToggleBtn", "fogBrushBtn"]
  static values = {
    cols:       { type: Number,  default: 20 },
    rows:       { type: Number,  default: 13 },
    zoomSpeed:  { type: Number,  default: 0.08 },
    stageId:    { type: Number,  default: 0 },
    fogEnabled: { type: Boolean, default: false },
    fogCells:   { type: Array,   default: [] },
  }

  connect() {
    this.scale = 1
    this.panX  = 0
    this.panY  = 0
    this.isPanning  = false
    this.fogMode    = false
    this.isPainting = false
    this.paintReveal = true

    // Local fog state — not using Stimulus values so we avoid JSON round-trips on every cell paint
    this._fogEnabled = this.fogEnabledValue
    this._fogCells   = this.fogCellsValue.length > 0
      ? [...this.fogCellsValue]
      : new Array(this.colsValue * this.rowsValue).fill(false)

    this._onWheel        = (e) => this.onWheel(e)
    this._onPointerDown  = (e) => this.onPointerDown(e)
    this._onPointerMove  = (e) => this.onPointerMove(e)
    this._onPointerUp    = (e) => this.onPointerUp(e)
    this._onPointerCancel= (e) => this.onPointerUp(e)
    this._onKeyDown      = (e) => this.onKeyDown(e)
    this._onDragStart    = (e) => e.preventDefault()

    this.element.addEventListener("wheel",         this._onWheel,        { passive: false })
    this.element.addEventListener("pointerdown",   this._onPointerDown)
    this.element.addEventListener("pointermove",   this._onPointerMove)
    this.element.addEventListener("pointerup",     this._onPointerUp)
    this.element.addEventListener("pointercancel", this._onPointerCancel)
    this.element.addEventListener("dragstart",     this._onDragStart)
    document.addEventListener("keydown",           this._onKeyDown)

    // Prevent text/image selection during pan gestures
    this.element.style.userSelect = "none"

    requestAnimationFrame(() => {
      this.fitToViewport()
      this.drawFog()
    })
  }

  disconnect() {
    this.element.removeEventListener("wheel",         this._onWheel)
    this.element.removeEventListener("pointerdown",   this._onPointerDown)
    this.element.removeEventListener("pointermove",   this._onPointerMove)
    this.element.removeEventListener("pointerup",     this._onPointerUp)
    this.element.removeEventListener("pointercancel", this._onPointerCancel)
    this.element.removeEventListener("dragstart",     this._onDragStart)
    document.removeEventListener("keydown",           this._onKeyDown)
  }

  // ── Public actions (HUD buttons) ─────────────────────────────────────

  fitToViewport() {
    const vw   = this.element.offsetWidth
    const vh   = this.element.offsetHeight
    const mapW = this.colsValue * CELL_SIZE
    const mapH = this.rowsValue * CELL_SIZE
    this.scale = Math.min(vw / mapW, vh / mapH) * 0.92
    this.panX  = (vw - mapW * this.scale) / 2
    this.panY  = (vh - mapH * this.scale) / 2
    this.applyTransform()
  }

  zoomIn() {
    this.zoomAround(this.element.offsetWidth / 2, this.element.offsetHeight / 2, 1.25)
  }

  zoomOut() {
    this.zoomAround(this.element.offsetWidth / 2, this.element.offsetHeight / 2, 1 / 1.25)
  }

  togglePanel() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.toggle("-translate-x-full")
    if (this.hasPanelChevronTarget) {
      this.panelChevronTarget.classList.toggle("rotate-180")
    }
  }

  // ── Zoom ─────────────────────────────────────────────────────────────

  onWheel(event) {
    if (event.target.closest("[data-hud]")) return
    event.preventDefault()
    const speed  = this.zoomSpeedValue
    const factor = event.deltaY < 0 ? 1 + speed : 1 / (1 + speed)
    this.zoomAround(event.clientX, event.clientY, factor)
  }

  zoomAround(cx, cy, factor) {
    const newScale = Math.max(0.1, Math.min(6, this.scale * factor))
    const ratio    = newScale / this.scale
    this.panX  = cx - ratio * (cx - this.panX)
    this.panY  = cy - ratio * (cy - this.panY)
    this.scale = newScale
    this.applyTransform()
  }

  // ── Pan ──────────────────────────────────────────────────────────────

  onPointerDown(event) {
    if (event.button !== 0) return
    // Let tokens, assets, HUD panels, and asset placement overlays handle their own events
    if (event.target.closest("[data-token-id]"))  return
    if (event.target.closest("[data-asset-id]"))  return
    if (event.target.closest("[data-hud]"))        return
    if (event.target.closest("[data-no-pan]"))     return

    this.element.setPointerCapture(event.pointerId)
    this.isPanning = true
    this.panStartX = event.clientX - this.panX
    this.panStartY = event.clientY - this.panY
    this.element.style.cursor = "grabbing"
  }

  onPointerMove(event) {
    if (!this.isPanning) return
    this.panX = event.clientX - this.panStartX
    this.panY = event.clientY - this.panStartY
    this.applyTransform()
  }

  onPointerUp(event) {
    if (!this.isPanning) return
    this.isPanning = false
    this.element.style.cursor = ""
    if (event && this.element.hasPointerCapture(event.pointerId)) {
      this.element.releasePointerCapture(event.pointerId)
    }
  }

  onKeyDown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "0") {
      event.preventDefault()
      this.fitToViewport()
    }
  }

  // ── Transform ────────────────────────────────────────────────────────

  applyTransform() {
    if (this.hasCanvasTarget) {
      this.canvasTarget.style.transform =
        `translate(${this.panX}px, ${this.panY}px) scale(${this.scale})`
    }
  }

  // ── Fog of War ───────────────────────────────────────────────────────

  toggleFog() {
    this._fogEnabled = !this._fogEnabled
    this.drawFog()
    this.saveFog()
    if (this.hasFogToggleBtnTarget) {
      this.fogToggleBtnTarget.textContent = this._fogEnabled ? "Fog: On" : "Fog: Off"
      this.fogToggleBtnTarget.classList.toggle("btn-gold",    this._fogEnabled)
      this.fogToggleBtnTarget.classList.toggle("btn-surface", !this._fogEnabled)
    }
  }

  toggleFogBrush() {
    this.fogMode = !this.fogMode
    if (this.hasFogPaintOverlayTarget) {
      this.fogPaintOverlayTarget.classList.toggle("hidden", !this.fogMode)
    }
    if (this.hasFogBrushBtnTarget) {
      this.fogBrushBtnTarget.classList.toggle("btn-gold",    this.fogMode)
      this.fogBrushBtnTarget.classList.toggle("btn-surface", !this.fogMode)
    }
  }

  revealAll() {
    this._fogCells = new Array(this.colsValue * this.rowsValue).fill(true)
    this.drawFog()
    this.saveFog()
  }

  hideAll() {
    this._fogCells = new Array(this.colsValue * this.rowsValue).fill(false)
    this.drawFog()
    this.saveFog()
  }

  // Paint overlay pointer events

  onFogPaintStart(event) {
    event.currentTarget.setPointerCapture(event.pointerId)
    this.isPainting  = true
    this.paintReveal = !event.shiftKey  // shift = re-fog
    this._paintCell(event)
  }

  onFogPaintMove(event) {
    if (!this.isPainting) return
    this._paintCell(event)
  }

  onFogPaintEnd(event) {
    if (!this.isPainting) return
    this.isPainting = false
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    this.saveFog()
  }

  _paintCell(event) {
    if (!this.hasFogCanvasTarget) return
    const rect = this.fogCanvasTarget.getBoundingClientRect()
    const col  = Math.floor((event.clientX - rect.left) / rect.width  * this.colsValue)
    const row  = Math.floor((event.clientY - rect.top)  / rect.height * this.rowsValue)
    const c    = Math.max(0, Math.min(this.colsValue - 1, col))
    const r    = Math.max(0, Math.min(this.rowsValue - 1, row))
    const idx  = r * this.colsValue + c
    if (this._fogCells[idx] === this.paintReveal) return  // no change
    this._fogCells[idx] = this.paintReveal
    this.drawFog()
  }

  drawFog() {
    if (!this.hasFogCanvasTarget) return
    const canvas = this.fogCanvasTarget
    const ctx    = canvas.getContext("2d")
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    if (!this._fogEnabled) return

    const cols  = this.colsValue
    const rows  = this.rowsValue
    const cellW = canvas.width  / cols
    const cellH = canvas.height / rows

    ctx.fillStyle = "rgba(0, 0, 0, 0.65)"  // semi-transparent on DM view
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (!this._fogCells[r * cols + c]) {
          ctx.fillRect(c * cellW, r * cellH, cellW + 1, cellH + 1)
        }
      }
    }
  }

  async saveFog() {
    if (!this.stageIdValue) return
    const res = await fetch(`/stages/${this.stageIdValue}/fog`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept":       "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
      },
      body: JSON.stringify({ fog_enabled: this._fogEnabled, fog_cells: this._fogCells }),
    })
    if (!res.ok) console.warn("fog save failed", res.status)
  }
}
